import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { Pie } from 'react-chartjs-2';
import { ArrowsIn } from '@phosphor-icons/react';
import { formatCurrency } from 'shared/utils/currency';
import { AcquisitionSourceBucket } from '../types/statistics.types';
import { CHART_COLORS, CHART_COLORS_ALPHA, COMMON_TOOLTIP } from './chartConfig';
import { MetricInfo } from './MetricInfo';
import './chartConfig';

interface Props {
  data?: AcquisitionSourceBucket[];
  loading: boolean;
}

export const SourceDistributionChart = ({ data, loading }: Props) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;
    const sorted = [...data].sort((a, b) => b.count - a.count);
    return {
      labels: sorted.map((d) => d.source),
      datasets: [
        {
          label: 'Dossiers',
          data: sorted.map((d) => d.count),
          backgroundColor: CHART_COLORS_ALPHA.slice(0, sorted.length),
          borderColor: CHART_COLORS.slice(0, sorted.length),
          borderWidth: 2,
        },
      ],
      _meta: sorted,
    };
  }, [data]);

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <ArrowsIn className="h-5 w-5 text-moss-primary" weight="duotone" />
            <Skeleton className="h-5 w-40" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ArrowsIn className="h-5 w-5 text-moss-primary" weight="duotone" />
          <CardTitle className="text-lg">Dossiers per bron</CardTitle>
          <MetricInfo
            description={
              <div className="space-y-1">
                <p>
                  Aantal dossiers per <code>acquisitionSource</code>. Hover voor
                  conversie en volume.
                </p>
                <p>
                  <strong>Conversie:</strong> deals / aantal dossiers van die bron.
                </p>
              </div>
            }
          />
        </div>
        <p className="text-xs text-neutral-secondary-mid">
          Waar komen onze deals vandaan? Conversie naar deal per bron
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          {chartData ? (
            <Pie
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'right',
                    labels: { boxWidth: 12, padding: 12, font: { size: 12 } },
                  },
                  tooltip: {
                    ...COMMON_TOOLTIP,
                    callbacks: {
                      label: (ctx: any) => {
                        const meta = chartData._meta[ctx.dataIndex];
                        const conv = meta.count > 0 ? (meta.deals / meta.count) * 100 : 0;
                        return [
                          `${meta.count} dossiers`,
                          `${meta.deals} deals (${conv.toFixed(0)}% conversie)`,
                          `Volume: ${formatCurrency(meta.totalVolume)}`,
                        ];
                      },
                    },
                  },
                },
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-secondary-mid">
              Geen data beschikbaar
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
